import {z} from 'zod';
import {
	editEffectsSortOrder,
	type EditEffectsSortOrderParams,
	getSortField,
	type GetSortFieldParams,
	getSortOrder,
	type GetSortOrderParams,
	SortOrder,
	SortSchema,
} from '@netx/core-schema';
import {
	AttributeVocabularyValueField,
	type IAttributeVocabularyValue,
} from './attributeVocabularyValueSchema';

export const ATTRIBUTE_VOCABULARY_VALUE_SORTABLE_FIELDS = [
	AttributeVocabularyValueField.assetCount,
	AttributeVocabularyValueField.ordinal,
	AttributeVocabularyValueField.value,
] as const;

export const AttributeVocabularyValueSortSchema = SortSchema.omit({
	field: true,
}).extend({
	field: z.enum(ATTRIBUTE_VOCABULARY_VALUE_SORTABLE_FIELDS),
});

export type AttributeVocabularyValueSort = z.infer<
	typeof AttributeVocabularyValueSortSchema
>;

export const AttributeVocabularyValueSortableField = z.enum(
	ATTRIBUTE_VOCABULARY_VALUE_SORTABLE_FIELDS,
).enum;

export const ATTRIBUTE_VOCABULARY_VALUE_DEFAULT_SORT_FIELD =
	AttributeVocabularyValueSortableField.ordinal;

export const ATTRIBUTE_VOCABULARY_VALUE_DEFAULT_SORT_ORDER =
	SortOrder.ASCENDING;

/** will return the sort passed in if valid/specified, otherwise will return the defaults for the invalid/unspecified */
export const getAttributeVocabularyValueDefaultSort = (
	sort: Partial<AttributeVocabularyValueSort> = {},
): AttributeVocabularyValueSort => ({
	field: getAttributeVocabularyValueSortField({sortField: sort.field}),
	order: getAttributeVocabularyValueSortOrder({sortOrder: sort.order}),
});

/** will return the passed in sort field if valid, otherwise will retutn the default sort field */
export const getAttributeVocabularyValueSortField = (
	params: Omit<
		GetSortFieldParams<
			(typeof ATTRIBUTE_VOCABULARY_VALUE_SORTABLE_FIELDS)[number]
		>,
		'defaultSortField' | 'sortableFields'
	>,
) =>
	getSortField({
		...params,
		sortableFields: ATTRIBUTE_VOCABULARY_VALUE_SORTABLE_FIELDS,
		defaultSortField: ATTRIBUTE_VOCABULARY_VALUE_DEFAULT_SORT_FIELD,
	});

/** will return the passed in sort order if valid, otherwise will return the default sort order */
export const getAttributeVocabularyValueSortOrder = (
	params: Omit<GetSortOrderParams, 'defaultSortOrder'>,
) =>
	getSortOrder({
		...params,
		defaultSortOrder: ATTRIBUTE_VOCABULARY_VALUE_DEFAULT_SORT_ORDER,
	});

/** given the original data, the new data, and the current sort, determine if the sort order was effected */
export const editAttributeVocabularyValueEffectsOrder = (
	params: Omit<
		EditEffectsSortOrderParams<
			IAttributeVocabularyValue,
			(typeof ATTRIBUTE_VOCABULARY_VALUE_SORTABLE_FIELDS)[number]
		>,
		'sortableFields'
	>,
) =>
	editEffectsSortOrder({
		...params,
		sortableFields: ATTRIBUTE_VOCABULARY_VALUE_SORTABLE_FIELDS,
	});
